import { useAtom } from "jotai";
import { backendDataAtom } from "atoms/backendDataAtom.js";
import { backendPost, backendPatch, backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useSnackbar } from "notistack";
import {
  setRelationState,
  setRelationSubKeyState,
  updateRelationState,
} from "./claimRelationStateHelpers.js";
import { useClaimActions } from "./useClaimActions.jsx";
import { useCallback, useState } from "react";

export function useClaimCommunicationActions() {
  const [backendData, setBackendData] = useAtom(backendDataAtom);
  const { enqueueSnackbar } = useSnackbar();

  const relationKey = "kommunikation";
  const relationMetaKey = "kommunikation_meta";
  const state = backendData?.relations?.[relationKey];

  const setState = (data) =>
    setRelationState(backendData, setBackendData, relationKey, data);

  const updateStateItem = (data) =>
    updateRelationState(backendData, setBackendData, relationKey, data);

  const setMetaKeyState = (subKey, data) =>
    setRelationSubKeyState(
      backendData,
      setBackendData,
      relationMetaKey,
      subKey,
      data
    );

  const { pushRelationItem: pushClaimRelation } = useClaimActions();

  const typeCounts =
    backendData.relations?.[relationMetaKey]?.type_counts || {};

  /**
   * Kommunikation erstellen
   *
   * @param formData
   * @param options
   * @returns {Promise<void>}
   */
  const createCommunication = async (formData, options = {}) => {
    const communication = await backendPost("data/kommunikation", {
      label: "Kommunikation erstellen",
      toSend: formData,
      enqueueSnackbar,
      setResult: ({ data }) => {
        updateStateItem(data);

        // Also push to claim relation prop
        pushClaimRelation("kommunikation", data);
      },
      ...options,
    });

    await fetchTypeCounts();

    return communication?.data;
  };

  /**
   * Kommunikation aktualisieren
   *
   * @param formData
   * @param options
   * @returns {Promise<void>}
   */
  const updateCommunication = async (formData, options = {}) => {
    await backendPatch("data/kommunikation", {
      label: "Kommunikation aktualisieren",
      toSend: formData,
      enqueueSnackbar,
      setResult: ({ data }) => {
        updateStateItem(data);
      },
      ...options,
    });

    await fetchTypeCounts();
  };

  /**
   * Kommunikation Reminder aktualisieren
   *
   * @param communicationId
   * @param formData
   * @param options
   * @returns {Promise<void>}
   */
  const updateCommunicationReminder = async (
    communicationId,
    formData,
    options = {}
  ) => {
    const { schaden_id } = formData;

    await backendPatch(
      `data/kommunikations/${schaden_id}/${communicationId}/reminder`,
      {
        toSend: formData,
        enqueueSnackbar,
        setResult: ({ data }) => {
          updateStateItem(data);
        },
        ...options,
      }
    );

    await fetchTypeCounts();
  };


  /**
   * Betreff ändern
   *
   * @param communicationId
   * @param formData
   * @param options
   * @returns {Promise<void>}
   */
  const updateCommunicationSubject = async (
    communicationId,
    formData,
    options = {}
  ) => {
    const { schaden_id } = formData;

    await backendPatch(
      `data/kommunikations/${schaden_id}/${communicationId}/subject`,
      {
        label: 'Bezeichnung ändern',
        toSend: formData,
        enqueueSnackbar,
        setResult: ({ data }) => {
          updateStateItem(data);
        },
        ...options,
      }
    );
  };


  const forwardCommunication = useCallback(
    async (communicationId, formData, options = []) => {
      return await backendPost(
        `data/kommunikations/${communicationId}/forward`,
        {
          toSend: formData,
          enqueueSnackbar,
          ...options,
        }
      );
    },
    [enqueueSnackbar]
  );

  const toggleCommunicationInternal = async (
    communication,
    internal,
    options = {}
  ) => {
    await backendPatch(
      `data/kommunikations/${communication.schaden_id}/${communication.id}/toggle-internal`,
      {
        toSend: {
          internal,
        },
        enqueueSnackbar,
        setResult: ({ data }) => {
          updateStateItem(data);
        },
        ...options,
      }
    );
  };

  const toggleCommunicationDone = async (communication, done, options = {}) => {
    await backendPatch(
      `data/kommunikations/${communication.schaden_id}/${communication.id}/toggle-done`,
      {
        toSend: {
          done,
        },
        enqueueSnackbar,
        setResult: ({ data }) => {
          updateStateItem(data);
        },
        ...options,
      }
    );
  };

  const fetchTypeCounts = async () => {
    const schaden_id = backendData.id;

    await backendGet(`/data/kommunikations/${schaden_id}/type-counts`, {
      label: "Kommunikationsstatistik",
      enqueueSnackbar,
      setResult: (response) => {
        setMetaKeyState("type_counts", response.data);
      },
      options: {
        params: {
          apiVersion: "v2",
        },
      },
    });
  };

  return {
    createCommunication,
    forwardCommunication,
    updateCommunication,
    updateCommunicationSubject,
    toggleCommunicationInternal,
    updateCommunicationReminder,
    toggleCommunicationDone,
    setState,
    state,
    typeCounts,
    fetchTypeCounts,
  };
}
